import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';

import CuratedLabel from 'components/search/curated-selection/curated-label';
import FriendlyLocationDistance from 'components/location/friendly-distance';
import { generateStaticMapUrl } from 'components/common/mini-map';
import CurrencySymbol from 'components/common/currency-symbol';

import Search from 'models/search';
import { Location as LocationModel } from 'models/locations';
import * as RoutingStyle from 'lib/routing-style';

import {
  CURATED_CARD_IMPRESSION,
  CURATED_CARD_TOOLTIP_IMPRESSION,
  CURATED_CARD_DETAILS_IMPRESSION,
  CURATED_CARD_PARK_HERE_IMPRESSION,
  CURATED_CARD_CLICK,
  CURATED_CARD_TOOLTIP_CLICK,
  CURATED_CARD_DETAILS_CLICK,
  CURATED_CARD_PARK_HERE_CLICK,
} from 'lib/analytics/events/index';

const propTypes = {
  label: PropTypes.string.isRequired,
  location: PropTypes.instanceOf(LocationModel).isRequired,
  trackEvent: PropTypes.func.isRequired,
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  appContext: PropTypes.string.isRequired,

  locationCount: PropTypes.number,
  routingStyle: PropTypes.string,
};

const defaultProps = {
  locationCount: 3,
  routingStyle: RoutingStyle.PARKWHIZ,
};

class Location extends Component {
  constructor(props) {
    super(props);

    this.onBookClick = this.onBookClick.bind(this);
    this.onDetailsClick = this.onDetailsClick.bind(this);
    this.onCurationCardVisibilityChange = this.onCurationCardVisibilityChange.bind(this);
    this.onToolTipVisibilityChange = this.onToolTipVisibilityChange.bind(this);
    this.onDetailButtonVisibilityChange = this.onDetailButtonVisibilityChange.bind(this);
    this.onParkHereButtonVisibilityChange = this.onParkHereButtonVisibilityChange.bind(this);
    this.onCurationCardClick = this.onCurationCardClick.bind(this);
    this.onToolTipClick = this.onToolTipClick.bind(this);
  }

  onCurationCardVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...CURATED_CARD_IMPRESSION,
        properties: this.trackingProperties,
      });
    }
  }

  onCurationCardClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this.props.trackEvent(CURATED_CARD_CLICK);
  }

  onToolTipVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...CURATED_CARD_TOOLTIP_IMPRESSION,
        properties: this.trackingProperties,
      });
    }
  }

  onToolTipClick() {
    this.props.trackEvent(CURATED_CARD_TOOLTIP_CLICK);
  }

  onDetailButtonVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...CURATED_CARD_DETAILS_IMPRESSION,
        properties: this.trackingProperties,
      });
    }
  }

  onParkHereButtonVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...CURATED_CARD_PARK_HERE_IMPRESSION,
        properties: this.trackingProperties,
      });
    }
  }

  onBookClick() {
    this.props.trackEvent({
      ...CURATED_CARD_PARK_HERE_CLICK,
      properties: this.trackingProperties,
    });
  }

  onDetailsClick() {
    this.props.trackEvent({
      ...CURATED_CARD_DETAILS_CLICK,
      properties: this.trackingProperties,
    });
  }

  get quotePrice() {
    const quote = this.props.location.getQuote();
    if (!quote) { return null; }

    return quote.price;
  }

  get priceArray() {
    if (!this.quotePrice) { return []; }

    return this.quotePrice.toString().split('.');
  }

  get trackingProperties() {
    return {
      CuratedClass: this.props.label,
      CuratedStyle: 'Map',
      LocationId: this.props.location.id,
      PricingId: this.props.location.quotes.first().id,
    };
  }

  renderPrice() {
    return (
      <div>
        <sup className="text-weight-book">
          <CurrencySymbol location={this.props.location} />
        </sup>
        { this.priceArray[0] }
        { this.priceArray.length === 2 ? <sup>{ this.priceArray[1] }</sup> : null }
      </div>
    );
  }

  renderImage() {
    const { location, currentSearch } = this.props;

    const desktopUrl = generateStaticMapUrl(
      location.entrances.first(),
      { lat: currentSearch.anchorLat, lng: currentSearch.anchorLng },
      '496x290',
    );
    const mobileUrl = generateStaticMapUrl(
      location.entrances.first(),
      { lat: currentSearch.anchorLat, lng: currentSearch.anchorLng },
      '200x200',
    );

    return (
      <picture>
        <source media="(max-width: 767px)" srcSet={mobileUrl} />
        <img src={desktopUrl} alt="" />
      </picture>
    );
  }

  render() {
    const { label, location, locationCount, currentSearch, appContext, routingStyle } = this.props;
    const displayMap = false;
    const quote = location.getQuote();
    const wrapperClassName = cx({
      'margin-bottom-10': true,
      'col-xs-12': true,
      'col-md-4': locationCount === 3,
      'col-md-6': locationCount <= 2,
      'col-centered': true,
      'top-pick': label.toLowerCase() === 'top pick',
    });

    return (
      <VisibilitySensor onChange={this.onCurationCardVisibilityChange} partialVisibility>
        <div className={wrapperClassName} onClick={this.onCurationCardClick}>
          <div className="background-white border-radius-4 shadowed padding-top-xs-10 padding-top-sm-0">
            <div className="row padding-bottom-10">
              <div className="col-xs-12 col-gutter-left-30 col-gutter-right-15 margin-vertical-10 hidden-xs">
                <div className="text-color-dark-slate text-weight-bold text-size-36 line-height-full display-inline-block">
                  { this.renderPrice() }
                </div>
                <div className="background-color-watermelon text-color-white text-weight-book text-size-10 padding-vertical-3 padding-horizontal-10 display-inline-block pull-right margin-top-10">
                  <VisibilitySensor onChange={this.onToolTipVisibilityChange} partialVisibility>
                    <CuratedLabel label={label} trackToolTipClick={this.onToolTipClick} />
                  </VisibilitySensor>
                </div>
              </div>
              <div className="curated-image col-xs-4 col-sm-12 col-gutter-right-xs-0 col-gutter-right-sm-15 col-gutter-left-xs-30 col-gutter-left-sm-15">
                { this.renderImage() }
              </div>
              <div className="col-xs-8 col-sm-12 col-gutter-left-0 col-gutter-left-sm-15 margin-top-sm-10">
                <div className="row visible-xs">
                  <div className="col-xs-12 col-gutter-left-30">
                    <div className="text-color-dark-slate text-weight-bold text-size-36 line-height-full display-inline-block">
                      { this.renderPrice() }
                    </div>
                    <div className="background-color-watermelon text-color-white text-weight-book text-size-10 padding-vertical-3 padding-horizontal-10 display-inline-block pull-right margin-top-5">
                      <CuratedLabel label={label} />
                    </div>
                  </div>
                </div>
                <div className="hidden-xs col-sm-2 col-gutter-right-0">
                  <span className="iconified-font iconified-pin text-color-blue" />
                </div>
                <div className="col-xs-12 col-sm-10 col-gutter-left-sm-0 text-size-12 text-color-dark-slate text-weight-medium wrap-ellipses">
                  { location.address }, { location.city } { location.state }
                </div>
                <div className="col-xs-12 col-sm-10 col-sm-offset-2 col-gutter-left-sm-0 text-size-10 text-color-dark-slate text-weight-light wrap-ellipses">
                  { location.name }
                </div>
                <div className="col-xs-12 col-sm-10 col-sm-offset-2 col-gutter-left-sm-0 text-size-10 text-color-dark-slate text-weight-light wrap-ellipses margin-top-xs-5 margin-top-sm-10 text-weight-medium">
                  <FriendlyLocationDistance location={location} />
                </div>
              </div>
            </div>
            <div className="row text-size-10 text-align-center text-weight-light clickable border-side-xs-top border-side-sm-none border-color-xs-light-grey border-style-xs-solid border-style-sm-none">
              <div className="col-xs-6 col-gutter-right-0">
                <VisibilitySensor onChange={this.onDetailButtonVisibilityChange} partialVisibility>
                  <Link
                    className="text-color-blue padding-vertical-10 display-block text-color-hover-blue"
                    to={{
                      pathname: currentSearch.route(location, displayMap, appContext, { routingStyle }),
                      state: {
                        app: { name: 'Search' },
                        search: {
                          currentSearch: currentSearch.merge({
                            selectedLocationId: location.id,
                            selectedQuoteId: quote.id,
                          }).toJSON(),
                          selectedLocation: JSON.stringify(location.toJSON()),
                          selectedQuote: JSON.stringify(quote.toJSON()),
                          locationId: location.id,
                          quoteId: quote.id,
                          displayMap,
                        },
                      },
                    }}
                    onClick={this.onDetailsClick}
                  >
                    DETAILS
                  </Link>
                </VisibilitySensor>
              </div>
              <div className="col-xs-6 col-gutter-left-0">
                <VisibilitySensor onChange={this.onParkHereButtonVisibilityChange} partialVisibility>
                  <Link
                    className="text-color-xs-blue text-color-white background-color-blue background-color-hover-blue padding-vertical-10 display-block text-color-hover-white border-radius-bottom-right-4"
                    to={{
                      pathname: '/reserve/',
                      search: `?quote_id=${quote.id}`,
                      state: {
                        search: {
                          currentSearch: currentSearch.toJSON(),
                          locationId: location.id,
                          quoteId: quote.id,
                          selectedLocation: location.toJSON(),
                          selectedQuote: quote.toJSON(),
                          displayMap,
                        },
                      },
                    }}
                    onClick={this.onBookClick}
                  >
                    PARK HERE <span className="iconified-font iconified-chevron-right margin-left-5" />
                  </Link>
                </VisibilitySensor>
              </div>
            </div>
          </div>
        </div>
      </VisibilitySensor>
    );
  }
}

Location.propTypes = propTypes;
Location.defaultProps = defaultProps;

export default Location;

